import { Button, TextInput, Group, Center, Text, Select, Checkbox, Alert } from "@mantine/core";
import { IconSearch, IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState, useMemo } from "react";
import { ContractListItem, AddressSystem, ContractResponse } from "../../types/UserRole";
import { getContractsByAddressSystem, getAllAddressSystems, deleteContracts } from "../../services/ContractService";
import { SortableTable, ColumnDef } from "../SortableTable";
import ContractForm from "./forms/fullPage/ContractForm";
import { notifications } from '@mantine/notifications';
import styles from './ContractsTable.module.css';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface ContractsTableProps {
    refreshContracts?: () => void;
}

export default function ContractsTable({ refreshContracts }: ContractsTableProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 326, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    const [contractSearch, setContractSearch] = useState<string>('');
    const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
    const [addressSystems, setAddressSystems] = useState<AddressSystem[]>([]);
    const [contracts, setContracts] = useState<ContractListItem[]>([]);
    const [selectedContracts, setSelectedContracts] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [selectedContract, setSelectedContract] = useState<ContractResponse | null>(null);
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
    const [pageSize, setPageSize] = useState(100);

    // Get address systems
    useEffect(() => {
        fetchAddressSystems();
    }, []);

    // Get contracts when address system is selected
    useEffect(() => {
        if (selectedAddressSystemId) {
            fetchContracts();
        } else {
            setContracts([]);
        }
        // Clear selected contracts when address system changes
        setSelectedContracts(new Set());
    }, [selectedAddressSystemId]);

    const fetchAddressSystems = async () => {
        try {
            const systems = await getAllAddressSystems();
            setAddressSystems(systems);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACTS_TABLE_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACTS_TABLE_LOAD_ADDRESS_SYSTEMS_FAILED") || "Failed to load address systems",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        }
    };

    // Dynamic page size calculation - now using static default of 100
    // useEffect(() => {
    //     function updatePageSize() {
    //         const availableHeight = window.innerHeight - 300;
    //         const rows = Math.max(3, Math.floor(availableHeight / 48));
    //         setPageSize(rows);
    //     }
    //     updatePageSize();
    //     window.addEventListener('resize', updatePageSize);
    //     return () => window.removeEventListener('resize', updatePageSize);
    // }, []);

    const fetchContracts = async () => {
        if (!selectedAddressSystemId) return;

        setLoading(true);
        try {
            const contractsData = await getContractsByAddressSystem(parseInt(selectedAddressSystemId));
            setContracts(contractsData);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACTS_TABLE_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACTS_TABLE_LOAD_CONTRACTS_FAILED") || "Failed to load contracts",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedContracts.size === 0) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACTS_TABLE_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACTS_TABLE_SELECT_DELETE_WARNING") || "Please select contracts to delete",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
            return;
        }

        try {
            await deleteContracts(Array.from(selectedContracts), parseInt(selectedAddressSystemId!));
            notifications.show({
                title: getTranslation("LABEL_CONTRACTS_TABLE_SUCCESS_TITLE") || "Success",
                message: getTranslation("LABEL_CONTRACTS_TABLE_DELETE_SUCCESS") || `${selectedContracts.size} contract(s) deleted successfully`,
                color: 'green',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-success'
            });
            setSelectedContracts(new Set());
            fetchContracts();
            refreshContracts?.();
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACTS_TABLE_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACTS_TABLE_DELETE_FAILED") || "Failed to delete contracts",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        }
    };

    const handleSelectContract = (contractId: number, checked: boolean) => {
        const newSelected = new Set(selectedContracts);
        if (checked) {
            newSelected.add(contractId);
        } else {
            newSelected.delete(contractId);
        }
        setSelectedContracts(newSelected);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedContracts(new Set(filteredContracts.map(c => c.id)));
        } else {
            setSelectedContracts(new Set());
        }
    };

    const handleAddNew = () => {
        setSelectedContract(null);
        setFormMode('create');
        setShowForm(true);
    };

    const handleEdit = (contract: ContractListItem) => {
        const contractResponse: ContractResponse = {
            id: contract.id,
            name: contract.name,
            contractorName: contract.contractorName,
            addressSystemId: parseInt(selectedAddressSystemId!),
            addressSystemName: addressSystems.find(as => as.id === parseInt(selectedAddressSystemId!))?.name || '',
            contractNumber: contract.contractNumber,
            contractFrameworkNumber: contract.contractFrameworkNumber,
            contractStatusId: contract.contractStatusId,
            contractStatusName: contract.contractStatusName,
            contractStatusDefaultValue: contract.contractStatusDefaultValue === 1 ? 1 : 0,
            archiveEmail: contract.archiveEmail,
            totalPrice: contract.totalPrice,
            totalArea: contract.totalArea,
            totalWeight: contract.totalWeight,
            defaultInvoicingNumber: contract.defaultInvoicingNumber,
            invoicingVatId: contract.invoicingVatId,
            invoicingPaymentTermId: contract.invoicingPaymentTermId,
            siteCode: contract.siteCode,
            littera: contract.littera,
            accountCode: contract.accountCode,
            archiveCode: contract.archiveCode,
            contractorArchiveEmail: contract.contractorArchiveEmail,
            changesApproverEmail: contract.changesApproverEmail,
            contractCity: contract.contractCity,
            contractorStreetAddress: contract.contractorStreetAddress,
            contractorCityAddress: contract.contractorCityAddress,
            contractorVatNumber: contract.contractorVatNumber,
            includeInReport: contract.includeInReport === 1 ? 1 : 0,
            copyToRamapp: contract.copyToRamapp === 1 ? 1 : 0,
            // userTemplateContractId: contract.userTemplateContractId,
            // templateContractId: contract.templateContractId,
        };
        setSelectedContract(contractResponse);
        setFormMode('edit');
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setSelectedContract(null);
        fetchContracts();
        refreshContracts?.();
    };

    const filteredContracts = useMemo(() => {
        if (!contractSearch.trim()) return contracts;

        const searchTerm = contractSearch.toLowerCase().trim();
        return contracts.filter((contract) => {
            const name = contract.name.toLowerCase();
            const contractorName = (contract.contractorName || '').toLowerCase();
            const contractNumber = (contract.contractNumber || '').toLowerCase();
            const cityName = (contract.contractCity || '').toLowerCase();
            const statusName = (contract.contractStatusName || '').toLowerCase();

            return name.includes(searchTerm) ||
                contractorName.includes(searchTerm) ||
                contractNumber.includes(searchTerm) ||
                cityName.includes(searchTerm) ||
                statusName.includes(searchTerm);
        });
    }, [contracts, contractSearch]);

    const columns: ColumnDef<ContractListItem>[] = [
        {
            header: (
                <Checkbox
                    checked={selectedContracts.size === filteredContracts.length && filteredContracts.length > 0}
                    indeterminate={selectedContracts.size > 0 && selectedContracts.size < filteredContracts.length}
                    onChange={(event) => handleSelectAll(event.currentTarget.checked)}
                />
            ),
            accessor: 'select',
            width: 60,
            render: (row) => (
                <Checkbox
                    checked={selectedContracts.has(row.id)}
                    onChange={(event) => handleSelectContract(row.id, event.currentTarget.checked)}
                />
            )
        },
        {
            header: getTranslation("LABEL_CONTRACTS_TABLE_NAME") || "Name",
            accessor: 'name',
            width: 200,
            sortable: true,
        },
        {
            header: getTranslation("LABEL_CONTRACTS_TABLE_CONTRACTOR") || "Contractor",
            accessor: 'contractorName',
            width: 150,
            render: (row) => row.contractorName || '-',
            sortable: true,
        },
        {
            header: getTranslation("LABEL_CONTRACTS_TABLE_CONTRACT_NUMBER") || "Contract Number",
            accessor: 'contractNumber',
            width: 150,
            render: (row) => row.contractNumber || '-',
            sortable: true,
        },
        {
            header: getTranslation("LABEL_CONTRACTS_TABLE_CITY_NAME") || "City Name",
            accessor: 'contractCity',
            width: 150,
            render: (row) => row.contractCity || '-',
            sortable: true,
        },
        {
            header: getTranslation("LABEL_CONTRACTS_TABLE_STATUS") || "Status",
            accessor: 'contractStatusName',
            width: 120,
            render: (row) => row.contractStatusName || '-',
            sortable: true,
        }
    ];

    if (showForm) {
        return (
            <ContractForm
                contract={selectedContract}
                mode={formMode}
                addressSystemId={parseInt(selectedAddressSystemId!)}
                onClose={handleFormClose}
                onBack={() => setShowForm(false)}
            />
        );
    }

    return (
        <>
            <Group justify="space-between" align="end" mb="md" className={styles.header}>
                <Select
                    label={getTranslation("LABEL_ADDRESS_SYSTEM") || "Address System"}
                    placeholder={getTranslation("PLACEHOLDER_SELECT_ADDRESS_SYSTEM") || "Select address system"}
                    data={addressSystems.map(as => ({ value: as.id.toString().trim(), label: as.name.trim() }))}
                    value={selectedAddressSystemId}
                    onChange={setSelectedAddressSystemId}
                    className={styles.addressSystemSelect}
                    clearable
                    searchable
                    spellCheck={false}
                />

                <TextInput
                    label={getTranslation("LABEL_SEARCH") || "Search"}
                    value={contractSearch}
                    onChange={(event) => setContractSearch(event.currentTarget.value)}
                    placeholder={getTranslation("PLACEHOLDER_SEARCH_CONTRACTS") || "Search contracts..."}
                    leftSection={<IconSearch size={18} color="#009DF0" />}
                    className={styles.searchInput}
                    disabled={!selectedAddressSystemId}
                    spellCheck={false}
                />

                <Group gap="xs">
                    <Button
                        onClick={handleDeleteSelected}
                        disabled={selectedContracts.size === 0}
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        className={styles.actionButton}
                    >
                        {getTranslation("LABEL_CONTRACTS_TABLE_DELETE") || "Delete"} ({selectedContracts.size})
                    </Button>
                    <Button
                        onClick={handleAddNew}
                        rightSection={<IconPlus size={16} />}
                        disabled={!selectedAddressSystemId}
                        className={styles.actionButton}
                    >
                        {getTranslation("LABEL_CONTRACTS_TABLE_ADD_NEW") || "Add New"}
                    </Button>
                </Group>
            </Group>

            {selectedAddressSystemId && (
                <>
                    {filteredContracts.length > 0 ? (
                        <SortableTable
                            data={filteredContracts}
                            pageSize={pageSize}
                            columns={columns}
                            rowKey={(row) => row.id}
                            actionColumns={[
                                {
                                    label: getTranslation("LABEL_CONTRACTS_TABLE_EDIT") || "Edit",
                                    width: 100,
                                    render: (row) => (
                                        <Button onClick={() => handleEdit(row)}>
                                            {getTranslation("LABEL_CONTRACTS_TABLE_EDIT") || "Edit"}
                                        </Button>
                                    )
                                }
                            ]}
                            rowStyle={(row) =>
                                selectedContracts.has(row.id)
                                    ? { backgroundColor: '#e3f2fd' }
                                    : {}
                            }
                        />
                    ) : !loading && (
                        <Alert icon={<IconAlertCircle size={18} />} color="blue">
                            {getTranslation("LABEL_CONTRACTS_TABLE_NONE_FOR_ADDRESS_SYSTEM") || "No contracts found for the selected address system."}
                        </Alert>
                    )}
                </>
            )}

            {!selectedAddressSystemId && (
                <Alert icon={<IconAlertCircle size={18} />} color="blue">
                    {getTranslation("LABEL_CONTRACTS_TABLE_SELECT_ADDRESS_SYSTEM") || "Please select an address system to continue."}
                </Alert>
            )}
        </>
    );
}
